import { nextTick, onBeforeUnmount, onMounted, type Ref, ref, watch } from "vue";

const checkHorizontalOverflow = (el: HTMLElement | null) => {
	if (!el) return false;
	return el.scrollWidth > el.clientWidth + 1;
};

export function useHorizontalOverflow(deps: (Ref<unknown> | Ref<unknown>[])[]) {
	const headerCardsContainer = ref<HTMLElement | null>(null);
	const productsCardsContainer = ref<HTMLElement | null>(null);
	const sizeListContainer = ref<HTMLElement | null>(null);
	const singleModifiersContainer = ref<HTMLElement | null>(null);
	const groupedModifiersContainers = ref<Record<string, HTMLElement | null>>({});

	const isHeaderCardsOverflowing = ref(false);
	const isProductsCardsOverflowing = ref(false);
	const isSizeOverflowing = ref(false);
	const isSingleModifiersOverflowing = ref(false);
	const groupedModifiersOverflowing = ref<Record<string, boolean>>({});

	const updateOverflowStates = () => {
		isHeaderCardsOverflowing.value = checkHorizontalOverflow(headerCardsContainer.value);
		isProductsCardsOverflowing.value = checkHorizontalOverflow(productsCardsContainer.value);
		isSizeOverflowing.value = checkHorizontalOverflow(sizeListContainer.value);
		isSingleModifiersOverflowing.value = checkHorizontalOverflow(singleModifiersContainer.value);
		const states: Record<string, boolean> = {};
		Object.entries(groupedModifiersContainers.value).forEach(([groupId, el]) => {
			states[groupId] = checkHorizontalOverflow(el || null);
		});
		groupedModifiersOverflowing.value = states;
	};

	const handleResize = () => {
		updateOverflowStates();
	};

	onMounted(() => {
		window.addEventListener("resize", handleResize);
		nextTick(updateOverflowStates);
	});

	onBeforeUnmount(() => {
		window.removeEventListener("resize", handleResize);
	});

	watch(
		deps,
		() => {
			nextTick(updateOverflowStates);
		},
		{ deep: true },
	);

	return {
		headerCardsContainer,
		productsCardsContainer,
		sizeListContainer,
		singleModifiersContainer,
		groupedModifiersContainers,
		isHeaderCardsOverflowing,
		isProductsCardsOverflowing,
		isSizeOverflowing,
		isSingleModifiersOverflowing,
		groupedModifiersOverflowing,
		updateOverflowStates,
	};
}